import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiUsers,
  FiGlobe,
  FiLock,
  FiImage,
  FiSettings,
  FiCheck,
  FiAlertCircle,
  FiPlus,
  FiBookOpen,
} from "react-icons/fi";
import { toast } from "react-toastify";
import communityService from "../../services/communityService";
import {
  validateCommunityName,
  validateCommunityDescription,
  validateImageUpload,
  sanitizeInput,
  logSecurityEvent,
  rateLimiter,
  handleSecureError,
} from "../../utils/securityUtils";
import "./styles/index.css";

// Animation variants
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 },
  },
};

const CreateCommunityModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    field: "",
    subField: "",
    subFields: [],
    type: "public",
    photoURL: null,
    allowMemberRoomCreation: false,
    requireApprovalForJoining: true,
    allowPublicViewing: true,
  });

  const fields = [
    "Computer Science",
    "Mathematics",
    "Physics",
    "Chemistry",
    "Biology",
    "Medicine",
    "Engineering",
    "Psychology",
    "Economics",
    "Philosophy",
    "Environmental Science",
    "Materials Science",
    "Neuroscience",
    "Biochemistry",
    "Astronomy",
    "Geology",
    "Anthropology",
    "Sociology",
    "Political Science",
    "Linguistics",
    "History",
    "Literature",
    "Art History",
    "Music",
    "Architecture",
    "Education",
    "Law",
    "Business",
    "Agriculture",
    "Veterinary Science",
  ];

  const subFieldsMap = {
    "Computer Science": [
      "Machine Learning",
      "Artificial Intelligence",
      "Data Science",
      "Software Engineering",
      "Computer Vision",
      "Natural Language Processing",
      "Cybersecurity",
      "Human-Computer Interaction",
      "Distributed Systems",
      "Algorithms",
      "Database Systems",
      "Computer Graphics",
    ],
    Mathematics: [
      "Pure Mathematics",
      "Applied Mathematics",
      "Statistics",
      "Mathematical Physics",
      "Number Theory",
      "Topology",
      "Analysis",
      "Algebra",
      "Geometry",
      "Combinatorics",
    ],
    Physics: [
      "Quantum Physics",
      "Theoretical Physics",
      "Experimental Physics",
      "Astrophysics",
      "Condensed Matter Physics",
      "Particle Physics",
      "Nuclear Physics",
      "Optics",
      "Thermodynamics",
    ],
    Chemistry: [
      "Organic Chemistry",
      "Inorganic Chemistry",
      "Physical Chemistry",
      "Analytical Chemistry",
      "Biochemistry",
      "Medicinal Chemistry",
      "Environmental Chemistry",
    ],
    Biology: [
      "Molecular Biology",
      "Cell Biology",
      "Genetics",
      "Ecology",
      "Evolutionary Biology",
      "Neurobiology",
      "Marine Biology",
      "Botany",
      "Microbiology",
    ],
    Medicine: [
      "Clinical Medicine",
      "Medical Research",
      "Public Health",
      "Pharmacology",
      "Pathology",
      "Cardiology",
      "Oncology",
      "Radiology",
    ],
    Engineering: [
      "Mechanical Engineering",
      "Electrical Engineering",
      "Civil Engineering",
      "Chemical Engineering",
      "Biomedical Engineering",
      "Aerospace Engineering",
      "Environmental Engineering",
    ],
    Psychology: [
      "Clinical Psychology",
      "Cognitive Psychology",
      "Social Psychology",
      "Developmental Psychology",
      "Neuropsychology",
    ],
    Economics: [
      "Microeconomics",
      "Macroeconomics",
      "Econometrics",
      "Behavioral Economics",
      "Development Economics",
    ],
    Philosophy: [
      "Ethics",
      "Metaphysics",
      "Logic",
      "Philosophy of Mind",
      "Political Philosophy",
      "Philosophy of Science",
    ],
    "Environmental Science": [
      "Climate Science",
      "Conservation Biology",
      "Environmental Policy",
      "Sustainability",
    ],
    "Materials Science": [
      "Nanotechnology",
      "Biomaterials",
      "Electronic Materials",
      "Polymers",
    ],
    Neuroscience: [
      "Cognitive Neuroscience",
      "Computational Neuroscience",
      "Behavioral Neuroscience",
    ],
    Biochemistry: [
      "Protein Biochemistry",
      "Enzymology",
      "Metabolic Pathways",
      "Structural Biology",
    ],
    Astronomy: [
      "Stellar Astronomy",
      "Galactic Astronomy",
      "Cosmology",
      "Planetary Science",
    ],
    Geology: ["Mineralogy", "Petrology", "Geochemistry", "Geophysics"],
    Anthropology: [
      "Cultural Anthropology",
      "Physical Anthropology",
      "Archaeology",
      "Linguistic Anthropology",
    ],
    Sociology: [
      "Social Theory",
      "Urban Sociology",
      "Criminology",
      "Social Psychology",
    ],
    "Political Science": [
      "International Relations",
      "Public Policy",
      "Comparative Politics",
      "Political Theory",
    ],
    Linguistics: ["Syntax", "Phonetics", "Semantics", "Sociolinguistics"],
    History: [
      "Ancient History",
      "Medieval History",
      "Modern History",
      "Cultural History",
    ],
    Literature: [
      "Comparative Literature",
      "Literary Theory",
      "Creative Writing",
      "Literary Criticism",
    ],
    "Art History": [
      "Renaissance Art",
      "Modern Art",
      "Contemporary Art",
      "Art Theory",
    ],
    Music: ["Music Theory", "Composition", "Performance", "Musicology"],
    Architecture: [
      "Urban Planning",
      "Sustainable Architecture",
      "Architectural History",
      "Interior Design",
    ],
    Education: [
      "Curriculum Development",
      "Educational Psychology",
      "Special Education",
      "Educational Technology",
    ],
    Law: [
      "Constitutional Law",
      "Criminal Law",
      "International Law",
      "Corporate Law",
    ],
    Business: ["Management", "Marketing", "Finance", "Entrepreneurship"],
    Agriculture: [
      "Crop Science",
      "Soil Science",
      "Agricultural Economics",
      "Sustainable Agriculture",
    ],
    "Veterinary Science": [
      "Small Animal Medicine",
      "Large Animal Medicine",
      "Veterinary Surgery",
      "Animal Pathology",
    ],
  };

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [activeSection, setActiveSection] = useState(0);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "field") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        subField: "",
        subFields: [],
      }));
    } else if (name === "subField") {
      // Auto-add subfield when selected from dropdown
      if (value && !formData.subFields.includes(value)) {
        setFormData((prev) => ({
          ...prev,
          subField: "",
          subFields: [...prev.subFields, value],
        }));
        // Clear subFields error when a subfield is added
        if (errors.subFields) {
          setErrors((prev) => ({ ...prev, subFields: "" }));
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAddSubfield = () => {
    if (formData.subField && !formData.subFields.includes(formData.subField)) {
      setFormData((prev) => ({
        ...prev,
        subFields: [...prev.subFields, formData.subField],
        subField: "",
      }));
    }
  };

  const handleRemoveSubfield = (subfield) => {
    setFormData((prev) => ({
      ...prev,
      subFields: prev.subFields.filter((sf) => sf !== subfield),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      validateImageUpload(file);
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewURL(e.target.result);
      };
      reader.readAsDataURL(file);

      logSecurityEvent("file_upload_initiated", {
        type: "community_photo",
        fileType: file.type,
        fileSize: file.size,
      });
    } catch (error) {
      toast.error(error.message);
      logSecurityEvent("file_upload_rejected", {
        reason: error.message,
        fileType: file.type,
        fileSize: file.size,
      });
    }
  };

  const removePhoto = () => {
    setSelectedFile(null);
    setPreviewURL(null);
    setFormData((prev) => ({ ...prev, photoURL: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateCommunityName(formData.name);
    if (nameError) {
      newErrors.name = nameError;
    }

    const descriptionError = validateCommunityDescription(formData.description);
    if (descriptionError) {
      newErrors.description = descriptionError;
    }

    if (!formData.field) {
      newErrors.field = "Field is required";
    }

    if (!formData.subFields || formData.subFields.length === 0) {
      newErrors.subFields = "At least one sub-field is required";
    }

    if (Object.keys(newErrors).length > 0) {
      logSecurityEvent("form_validation_failed", {
        form: "create_community",
        errors: Object.keys(newErrors),
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form first - validation failures shouldn't count against rate limit
    if (!validateForm()) {
      return;
    }

    // Only check rate limit after validation passes
    if (!rateLimiter.isAllowed("create_community", 3, 3600000)) {
      toast.error(
        "Too many community creation attempts. Please wait before trying again."
      );
      return;
    }

    try {
      setIsSubmitting(true);

      let photoURL = null;

      const communityData = {
        name: sanitizeInput(formData.name.trim()),
        description: sanitizeInput(formData.description.trim()),
        field: sanitizeInput(formData.field),
        subField: formData.subFields.map((field) => sanitizeInput(field)),
        type: formData.type,
        photoURL,
        allowMemberRoomCreation: formData.allowMemberRoomCreation,
        requireApprovalForJoining: formData.requireApprovalForJoining,
        allowPublicViewing: formData.allowPublicViewing,
      };

      logSecurityEvent("community_creation_attempt", {
        nameLength: communityData.name.length,
        descriptionLength: communityData.description.length,
        type: communityData.type,
        hasPhoto: !!selectedFile,
      });

      const response = await communityService.createCommunity(communityData);

      if (selectedFile && response.success) {
        try {
          await communityService.uploadCommunityPhoto(
            response.data.community._id,
            selectedFile
          );
          logSecurityEvent("community_photo_uploaded", {
            communityId: response.data.community._id,
          });
        } catch (photoError) {
          logSecurityEvent("community_photo_upload_failed", {
            communityId: response.data.community._id,
            error: photoError.message,
          });
          toast.warn("Community created successfully, but photo upload failed");
        }
      }

      logSecurityEvent("community_created_successfully", {
        communityId: response.data.community._id,
      });

      onSuccess(response);
    } catch (error) {
      const secureErrorMessage = handleSecureError(error);

      logSecurityEvent("community_creation_failed", {
        error: secureErrorMessage,
        status: error.status,
      });

      if (error.status === 400 && error.validationErrors) {
        const fieldErrors = {};
        error.validationErrors.forEach((err) => {
          fieldErrors[err.path] = err.msg;
        });
        setErrors(fieldErrors);
      } else {
        toast.error(secureErrorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="collab-comm-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div
          className="collab-comm-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="collab-comm-modal-header">
            <div className="collab-comm-modal-title">
              <div
                className="collab-comm-header-icon"
                style={{ width: 40, height: 40 }}
              >
                <FiUsers size={20} />
              </div>
              <h2>Create Community</h2>
            </div>
            <button className="collab-comm-modal-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="collab-comm-modal-body">
            <form id="create-community-form" onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <div className="collab-comm-form-section">
                <div className="collab-comm-form-section-header">
                  <FiBookOpen size={18} />
                  <h3 className="collab-comm-form-section-title">
                    Basic Information
                  </h3>
                </div>

                <div className="collab-comm-form-group">
                  <label className="collab-comm-form-label">
                    Community Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter community name (3-100 characters)"
                    className={`collab-comm-form-input ${
                      errors.name ? "error" : ""
                    }`}
                    maxLength={100}
                  />
                  {errors.name && (
                    <div className="collab-comm-form-error">
                      <FiAlertCircle size={14} />
                      {errors.name}
                    </div>
                  )}
                  <div className="collab-comm-form-hint">
                    {formData.name.length}/100 characters
                  </div>
                </div>

                <div className="collab-comm-form-group">
                  <label className="collab-comm-form-label">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your community (10-500 characters)"
                    className={`collab-comm-form-textarea ${
                      errors.description ? "error" : ""
                    }`}
                    rows={4}
                    maxLength={500}
                  />
                  {errors.description && (
                    <div className="collab-comm-form-error">
                      <FiAlertCircle size={14} />
                      {errors.description}
                    </div>
                  )}
                  <div className="collab-comm-form-hint">
                    {formData.description.length}/500 characters
                  </div>
                </div>

                <div className="collab-comm-form-group">
                  <label className="collab-comm-form-label">
                    Research Field *
                  </label>
                  <select
                    name="field"
                    value={formData.field}
                    onChange={handleInputChange}
                    className={`collab-comm-form-select ${
                      errors.field ? "error" : ""
                    }`}
                  >
                    <option value="">Select a field</option>
                    {fields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                  {errors.field && (
                    <div className="collab-comm-form-error">
                      <FiAlertCircle size={14} />
                      {errors.field}
                    </div>
                  )}
                </div>

                <div className="collab-comm-form-group">
                  <label className="collab-comm-form-label">Sub-fields *</label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <select
                      name="subField"
                      value={formData.subField}
                      onChange={handleInputChange}
                      className="collab-comm-form-select"
                      disabled={!formData.field}
                      style={{ flex: 1 }}
                    >
                      <option value="">
                        {formData.field
                          ? "Select a sub-field"
                          : "Select field first"}
                      </option>
                      {formData.field &&
                        subFieldsMap[formData.field]
                          ?.filter((sf) => !formData.subFields.includes(sf))
                          ?.map((subField) => (
                            <option key={subField} value={subField}>
                              {subField}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      className="collab-comm-btn collab-comm-btn-primary"
                      onClick={handleAddSubfield}
                      disabled={!formData.subField}
                      style={{ padding: "8px 16px" }}
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>

                  {formData.subFields.length > 0 && (
                    <div className="collab-comm-tags">
                      {formData.subFields.map((subfield) => (
                        <span key={subfield} className="collab-comm-tag">
                          {subfield}
                          <button
                            type="button"
                            className="collab-comm-tag-remove"
                            onClick={() => handleRemoveSubfield(subfield)}
                          >
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {errors.subFields && (
                    <div className="collab-comm-form-error">
                      <FiAlertCircle size={14} />
                      {errors.subFields}
                    </div>
                  )}
                </div>

                <div className="collab-comm-form-group">
                  <label className="collab-comm-form-label">
                    Community Type
                  </label>
                  <div className="collab-comm-radio-group">
                    <label
                      className={`collab-comm-radio-option ${
                        formData.type === "public" ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="public"
                        checked={formData.type === "public"}
                        onChange={handleInputChange}
                      />
                      <FiGlobe
                        size={18}
                        style={{ color: "var(--collab-green)" }}
                      />
                      <div className="collab-comm-radio-content">
                        <p className="collab-comm-radio-title">Public</p>
                        <p className="collab-comm-radio-desc">
                          Anyone can find and view this community
                        </p>
                      </div>
                    </label>
                    <label
                      className={`collab-comm-radio-option ${
                        formData.type === "private" ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value="private"
                        checked={formData.type === "private"}
                        onChange={handleInputChange}
                      />
                      <FiLock
                        size={18}
                        style={{ color: "var(--collab-amber)" }}
                      />
                      <div className="collab-comm-radio-content">
                        <p className="collab-comm-radio-title">Private</p>
                        <p className="collab-comm-radio-desc">
                          Only members can see and access this community
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Photo Section */}
              <div className="collab-comm-form-section">
                <div className="collab-comm-form-section-header">
                  <FiImage size={18} />
                  <h3 className="collab-comm-form-section-title">
                    Community Photo
                  </h3>
                  <span className="collab-comm-form-section-optional">
                    Optional
                  </span>
                </div>

                <div className="collab-comm-photo-upload">
                  {previewURL ? (
                    <div className="collab-comm-photo-preview">
                      <img src={previewURL} alt="Preview" />
                      <button
                        type="button"
                        className="collab-comm-photo-remove"
                        onClick={removePhoto}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ) : (
                    <label className="collab-comm-photo-placeholder">
                      <FiImage size={32} />
                      <span>Click to upload photo</span>
                      <small>Max 5MB • JPG, PNG, GIF</small>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        hidden
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Settings Section */}
              <div className="collab-comm-form-section">
                <div className="collab-comm-form-section-header">
                  <FiSettings size={18} />
                  <h3 className="collab-comm-form-section-title">
                    Community Settings
                  </h3>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <label
                    className={`collab-comm-checkbox-option ${
                      formData.requireApprovalForJoining ? "selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="requireApprovalForJoining"
                      checked={formData.requireApprovalForJoining}
                      onChange={handleInputChange}
                    />
                    <div className="collab-comm-radio-content">
                      <p className="collab-comm-radio-title">
                        Require approval for joining
                      </p>
                      <p className="collab-comm-radio-desc">
                        New members must be approved by moderators
                      </p>
                    </div>
                  </label>

                  <label
                    className={`collab-comm-checkbox-option ${
                      formData.allowPublicViewing ? "selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="allowPublicViewing"
                      checked={formData.allowPublicViewing}
                      onChange={handleInputChange}
                    />
                    <div className="collab-comm-radio-content">
                      <p className="collab-comm-radio-title">
                        Allow public viewing
                      </p>
                      <p className="collab-comm-radio-desc">
                        Non-members can view community content
                      </p>
                    </div>
                  </label>

                  <label
                    className={`collab-comm-checkbox-option ${
                      formData.allowMemberRoomCreation ? "selected" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="allowMemberRoomCreation"
                      checked={formData.allowMemberRoomCreation}
                      onChange={handleInputChange}
                    />
                    <div className="collab-comm-radio-content">
                      <p className="collab-comm-radio-title">
                        Allow members to create rooms
                      </p>
                      <p className="collab-comm-radio-desc">
                        Members can create their own research rooms
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </form>
          </div>

          {/* Modal Footer - Fixed at bottom */}
          <div className="collab-comm-modal-footer">
            <button
              type="button"
              className="collab-comm-btn collab-comm-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-community-form"
              className="collab-comm-btn collab-comm-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div
                    className="collab-comm-spinner"
                    style={{ width: 16, height: 16, borderWidth: 2 }}
                  />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck size={16} />
                  Create Community
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreateCommunityModal;
